/**
 * The regulatory frameworks an assessment may cite, and how publications spell them.
 *
 * **This is Joanne's vocabulary, and until now it was a file only Craig could change.** It decides
 * one thing: whether a client's proportionality basis is printed on an assessment's header block.
 * The basis is a fact about the *firm* — the framework she calibrates their compliance under — and
 * it is cited only where the publication is actually about that framework, so that an MLR citation
 * never appears on an SM&CR paper again.
 *
 * The aliases are the whole point, because nobody spells these the same way. She writes
 * "MLR 2017"; the instrument writes "the Money Laundering, Terrorist Financing and Transfer of
 * Funds (Information on the Payer) Regulations 2017"; the FCA writes "the Money Laundering
 * Regulations". A spelling that is missing here is a citation that should have appeared and did
 * not — and she is the one who knows which spellings exist.
 *
 * **Two guards, enforced by the API rather than by this form.** An alias must be long enough to be
 * a reference, and it must not appear in ordinary regulatory prose — "CONC" hides inside
 * *concerns*, "DISP" inside *dispute*. Both have already gone wrong once. The refusal comes back
 * as a sentence and is shown against the row rather than swallowed.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import type { Framework } from "../types";

/** A blank row, for adding one. */
const EMPTY: Framework = { name: "", title: "", aliases: [] };

function aliasesToText(aliases: string[]): string {
  return aliases.join("\n");
}

function textToAliases(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function Editor({
  framework,
  busy,
  onSave,
  onCancel,
  onDelete,
}: {
  framework: Framework;
  busy: boolean;
  onSave: (fields: { name: string; title: string; aliases: string[] }) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(framework.name);
  const [title, setTitle] = useState(framework.title);
  const [aliases, setAliases] = useState(aliasesToText(framework.aliases));
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div className="framework-editor">
      <label>
        Short name
        <input
          value={name}
          maxLength={60}
          placeholder="MLR 2017"
          onChange={(event) => setName(event.target.value)}
        />
        <span className="small muted">How you write it in a client&rsquo;s proportionality basis.</span>
      </label>

      <label>
        Full title
        <input
          value={title}
          maxLength={200}
          placeholder="The Money Laundering Regulations 2017"
          onChange={(event) => setTitle(event.target.value)}
        />
        <span className="small muted">For your reference only — it is never matched against.</span>
      </label>

      <label>
        Spellings, one per line
        <textarea
          rows={6}
          value={aliases}
          placeholder={"MLR 2017\nMoney Laundering Regulations\nMoney Laundering, Terrorist Financing and Transfer of Funds"}
          onChange={(event) => setAliases(event.target.value)}
        />
        <span className="small muted">
          Every way a publication might name this framework. A document using any one of them
          counts as being about it. Matched as whole words and ignoring capitals, so a bare
          acronym that is also part of an ordinary word will be refused — put its chapter number
          on it instead.
        </span>
      </label>

      <div className="row confirm-actions">
        <button
          type="button"
          className="primary"
          disabled={busy || !name.trim()}
          onClick={() => onSave({ name: name.trim(), title: title.trim(), aliases: textToAliases(aliases) })}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" className="link" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <span className="spacer" />
        {onDelete &&
          (confirmingDelete ? (
            <span className="confirm-delete">
              <span className="small">Remove {framework.name}?</span>
              <button type="button" className="danger" disabled={busy} onClick={onDelete}>
                Remove
              </button>
              <button
                type="button"
                className="link"
                disabled={busy}
                onClick={() => setConfirmingDelete(false)}
              >
                cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="link danger-link"
              disabled={busy}
              onClick={() => setConfirmingDelete(true)}
            >
              Remove
            </button>
          ))}
      </div>
    </div>
  );
}

export function FrameworkVocabulary({ onError }: { onError: (error: unknown) => void }) {
  const [items, setItems] = useState<Framework[] | null>(null);
  const [seeded, setSeeded] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  /** A refusal from the API, shown against the row that caused it rather than as a red box. */
  const [refusal, setRefusal] = useState<string>("");

  const load = useCallback(() => {
    api
      .listFrameworks()
      .then((page) => {
        setItems(page.items);
        setSeeded(page.seeded);
      })
      .catch(onError);
  }, [onError]);

  useEffect(load, [load]);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setRefusal("");
    try {
      await work();
      setEditing(null);
      setAdding(false);
      load();
    } catch (error) {
      // A validation refusal is the API teaching her something about aliases, not a fault. It
      // belongs beside the field she typed, not in the page-level error banner.
      const message = error instanceof Error ? error.message : String(error);
      setRefusal(message);
    } finally {
      setBusy(false);
    }
  };

  if (items === null) return null;

  return (
    <div className="framework-vocabulary">
      <div className="row documents-head">
        <h3>Frameworks an assessment can cite</h3>
        {!adding && !editing && (
          <button className="link" onClick={() => { setAdding(true); setRefusal(""); }}>
            Add a framework
          </button>
        )}
      </div>

      <p className="muted small">
        When a client&rsquo;s firm profile names a proportionality basis — &ldquo;MLR 2017 Reg.4 and
        FCA FCG 2&rdquo; — an impact assessment prints it in the header only if the publication is
        actually about that framework. This list is how it tells. The spellings matter because
        nobody writes these the same way: you write <em>MLR 2017</em>, an instrument writes the full
        legal title, the FCA writes <em>the Money Laundering Regulations</em>. A spelling missing
        here is a citation that should have appeared and did not.
      </p>

      {!seeded && (
        <p className="small ai-warning">
          <strong>These are the built-in defaults and cannot be edited yet.</strong> Ask Craig to
          run <code>make seed-frameworks</code> once; after that they are yours to change and
          nothing here is overwritten again.
        </p>
      )}

      {refusal && (
        <p className="small ai-warning">
          <strong>Not saved.</strong> {refusal}
        </p>
      )}

      {adding && (
        <Editor
          framework={EMPTY}
          busy={busy}
          onSave={(fields) => run(() => api.createFramework(fields))}
          onCancel={() => { setAdding(false); setRefusal(""); }}
        />
      )}

      <table className="documents frameworks">
        <thead>
          <tr>
            <th scope="col">Framework</th>
            <th scope="col">Spellings a publication might use</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {items.map((framework) => {
            const id = framework.id ?? framework.name;
            if (editing === id) {
              return (
                <tr key={id}>
                  <td colSpan={3}>
                    <Editor
                      framework={framework}
                      busy={busy}
                      onSave={(fields) => run(() => api.patchFramework(id, fields))}
                      onCancel={() => { setEditing(null); setRefusal(""); }}
                      onDelete={() => run(() => api.deleteFramework(id))}
                    />
                  </td>
                </tr>
              );
            }
            return (
              <tr key={id}>
                <td>
                  <strong>{framework.name}</strong>
                  {framework.title && <div className="muted small">{framework.title}</div>}
                </td>
                <td className="small">
                  {framework.aliases.map((alias) => (
                    <div key={alias}>{alias}</div>
                  ))}
                </td>
                <td className="delete-cell">
                  {seeded && (
                    <button
                      type="button"
                      className="link"
                      onClick={() => { setEditing(id); setRefusal(""); }}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="small muted">
        Removing a framework does not break anything that has already been written — it only means
        a client whose basis names it stops being recognised, and the assessment screen will say so
        before you spend anything.
      </p>
    </div>
  );
}
